import { UserName } from "@/components/UserName";
import { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { NotificationBell } from "@/components/NotificationBell";
import { SoundToggle } from "@/components/SoundToggle";
import { SurfaceToggle } from "@/components/SurfaceToggle";
import { FloorReturnBar } from "@/components/FloorReturnBar";
import { useFloorReturn } from "@/lib/floorReturn";
import { isPrintActive } from "@/lib/printActive";
import { FloorNudge } from "@/components/FloorNudge";
import { BackupHealthBadge } from "@/components/BackupHealthBadge";
import { ServerStatusBar } from "@/components/ServerStatus";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LOGO_FULL, LOGO_WHITE, BRAND, DIRECTIONS, getIdentity } from "@/lib/identity";
import { formatTime, getYardTz } from "@/lib/datetime";
import {
  MapPin, ScrollText, Users, SlidersHorizontal, History, LogOut, GitCompareArrows, Mail, SpellCheck,
  Truck, Package, ScanLine, Search, FileSpreadsheet, DatabaseBackup, KeyRound,
  Home, Camera, TrendingUp, ChevronDown, ChevronRight, Menu, X, Tags, ShoppingCart, Bell, RotateCcw, ArrowRightLeft, ArrowLeft, Inbox,
  PackagePlus, ListChecks, Boxes, ShieldCheck, Stethoscope, Images, ClipboardCheck, Sparkles, Move, Building2, Recycle, PackageOpen, Printer,
  Smartphone, PackageMinus, Wrench, SearchX, BookA, BadgeDollarSign,
} from "lucide-react";
import { useSales } from "@/lib/sales";

const GROUPS = [
  { items: [{ to: "/home", label: "Home", icon: Home, perm: null, testid: "nav-home" }] },
  { title: "Vehicles & Equipment", items: [
    { to: "/donors", label: "Vehicles & Equipment", icon: Truck, perm: "search_inventory", testid: "nav-donors", resumable: true },
  ] },
  { title: "Parts Management", items: [
    { to: "/quick-scan", label: "Scan / Look up", icon: Search, perm: "view_parts", testid: "nav-quick-scan" },
    { to: "/capture", label: "Tag Parts", icon: PackagePlus, perm: "manage_parts", testid: "nav-capture" },
    { to: "/rapid-capture", label: "Rapid Photo Capture", icon: ScanLine, perm: "manage_parts", testid: "nav-rapid-capture" },
    { to: "/sort-queue", label: "Identify & Completion", icon: ListChecks, perm: "manage_parts", testid: "nav-sort-queue" },
    { to: "/review-queue", label: "Admin Review", icon: ClipboardCheck, perm: "review_parts", testid: "nav-review-queue" },
    { to: "/ask-ai-guide", label: "Ask AI — crew guide", icon: Sparkles, perm: null, testid: "nav-ask-ai-guide" },
    { to: "/putaway", label: "Put Away", icon: ScanLine, perm: "putaway", testid: "nav-putaway" },
    { to: "/work-bin", label: "Work a Bin", icon: PackageOpen, perm: "putaway", testid: "nav-work-bin", hidden: true },
    { to: "/move-tote", label: "Move Container", icon: Boxes, perm: "putaway", testid: "nav-move-tote", hidden: true },
    { to: "/move-item", label: "Move Item", icon: Move, perm: "putaway", testid: "nav-move-item", hidden: true },
    { to: "/moves-history", label: "Moves History", icon: History, perm: "putaway", testid: "nav-moves-history" },
    { to: "/make-tags", label: "Parts Tags and Stickers", icon: Tags, perm: "generate_print_labels", testid: "nav-make-tags" },
    { to: "/pull-carts", label: "Carts and Orders", icon: ShoppingCart, perm: "build_cart", testid: "nav-pull-carts", badgeKey: "needs_completion", badgeClass: "bg-amber-500 text-white" },
    { to: "/pick", label: "Pick & Pull Parts", icon: PackageMinus, perm: "pick_remove", testid: "nav-pick" },
    { to: "/returns", label: "Returns", icon: RotateCcw, perm: "process_return", testid: "nav-returns" },
    { to: "/recycle-batches", label: "Recycling Batches", icon: Recycle, perm: "scrap_writeoff", testid: "nav-recycle-batches" },
    { to: "/transfers", label: "Part Transfers", icon: ArrowRightLeft, perm: "view_parts", testid: "nav-transfers" },
    { to: "/movement-history", label: "Where Did It Go? (Shipments)", icon: GitCompareArrows, perm: "search_inventory", testid: "nav-movement-history" },
    { to: "/requests", label: "Requests", icon: Inbox, perm: "submit_request", testid: "nav-requests", badgeKey: "requests" },
  ] },
  { title: "Other photo methods", items: [
    { to: "/tag-to-photos", label: "Tag-to-Photos (legacy)", icon: Images, perm: "use_legacy_photo_tools", testid: "nav-tag-to-photos" },
    { to: "/photo-upload", label: "Bulk Photo Upload (legacy)", icon: Camera, perm: "use_legacy_photo_tools", testid: "nav-dismantle" },
  ] },
  { title: "Inventory", items: [
    { to: "/search", label: "Find a Part", icon: Search, perm: "search_inventory", testid: "nav-search", resumable: true },
    { to: "/parts", label: "All Parts", icon: Package, perm: "search_inventory", testid: "nav-parts", resumable: true },
    { to: "/bulk-fix", label: "Bulk-Fix Numbers", icon: ShieldCheck, perm: "bulk_edit_parts", testid: "nav-bulk-fix" },
  ] },
  { title: "Facility", defaultOpen: true, items: [
    // THREE DOORS INTO ONE (his instruction 2026-10-09). Two of them were the same screen wearing
    // two hats, and the third existed only to print — which is now a menu on the page he is
    // already standing on. Every old link still works and opens the right tab.
    { to: "/places", label: "Places & Containers", icon: Boxes, perm: "view_locations", testid: "nav-places", resumable: true, badgeKey: "problems", badgeClass: "bg-amber-400 text-amber-950" },
    { to: "/fixed-locations", label: "Fixed Locations (shelves & yard spots)", icon: Building2, perm: "view_locations", testid: "nav-fixed-locations", hidden: true },
    { to: "/containers", label: "Storage Containers (totes & crates that move)", icon: Boxes, perm: "view_locations", testid: "nav-locations", hidden: true },
    { to: "/shelf-labels", label: "Fixed Location Labels", icon: MapPin, perm: "generate_print_labels", testid: "nav-shelf-labels", hidden: true },
  ] },
  { title: "Yard Production", items: [
    { to: "/yard-production", label: "Yard Metrics", icon: TrendingUp, perm: "view_operations_metrics", testid: "nav-yard-production" },
  ] },
  // STAGE 2 (B2.1, his plan 30 Oct): ONE new group with ONE entry. Absent while Stage 2 is switched off.
  { title: "Sales", items: [
    { to: "/sales", label: "Sales Management", icon: BadgeDollarSign, perm: "sales", testid: "nav-sales", resumable: true },
  ] },
  { title: "Admin & Tools", collapsible: true, items: [
    { to: "/notifications", label: "Notifications", icon: Bell, perm: "manage_settings", testid: "nav-notifications" },
    { to: "/data", label: "Data Export", icon: FileSpreadsheet, perm: "export_data", testid: "nav-data" },
    { to: "/users", label: "Users", icon: Users, perm: "manage_users", testid: "nav-users" },
    { to: "/permissions", label: "Permissions", icon: SlidersHorizontal, perm: "manage_roles_permissions", testid: "nav-permissions" },
    { to: "/managed-lists", label: "Managed Lists", icon: ListChecks, perm: "manage_lists", testid: "nav-managed-lists" },
    { to: "/naming", label: "Naming Consistency", icon: SpellCheck, perm: "manage_lists", testid: "nav-naming" },
    { to: "/vehicle-setup", label: "Vehicle Setup", icon: Truck, perm: "manage_lists", testid: "nav-vehicle-setup" },
    // CATCH-UP TOOLS (his ruling 2026-10-17): BOTH bulk tools in ONE place, office only, and the
    // entry is absent entirely without `use_catch_up_tools` — which no role holds by default.
    { to: "/catch-up", label: "Catch-up tools", icon: Wrench, perm: "use_catch_up_tools", testid: "nav-catch-up" },
    // SEARCH S1 (his calls, 24-25 Oct): both absent without their toggle, which no role holds by default.
    { to: "/search-record", label: "Search record", icon: SearchX, perm: "view_search_record", testid: "nav-search-record" },
    { to: "/search-synonyms", label: "Search synonyms", icon: BookA, perm: "approve_search_synonyms", testid: "nav-search-synonyms" },
    { to: "/printer-settings", label: "Printer Setup", icon: Printer, perm: "generate_print_labels", testid: "nav-printer-settings" },
    { to: "/floor-buttons", label: "Floor Buttons (tablet surface)", icon: Smartphone, perm: "manage_lists", testid: "nav-floor-buttons" },
    { to: "/taxonomy-audit", label: "List Audit", icon: GitCompareArrows, perm: "manage_taxonomy", testid: "nav-taxonomy-audit" },
    { to: "/ai-settings", label: "AI Assist", icon: Sparkles, perm: "manage_ai_budget", testid: "nav-ai-settings" },
    { to: "/daily-digest", label: "Daily Digest", icon: Mail, perm: "manage_ai_budget", testid: "nav-daily-digest" },
    { to: "/backups", label: "Backups", icon: DatabaseBackup, perm: "manage_backups", testid: "nav-backups" },
    { to: "/photo-maintenance", label: "Photo Maintenance", icon: Images, perm: "archive_photos", testid: "nav-photo-maintenance" },
    { to: "/container-lists", label: "Container Types & Colors", icon: Boxes, perm: "manage_container_lists", testid: "nav-container-lists" },
    // NOT an admin tool — it is YOUR OWN password and 2FA backup codes, and it is deliberately
    // open to everyone (perm: null). It sits here because the app has no personal/account area to
    // move it to yet, so the label does the work instead of a new one-item section.
    { to: "/security", label: "My Password & 2FA Codes", icon: KeyRound, perm: null, testid: "nav-security" },
    { to: "/audit", label: "Audit Log", icon: ScrollText, perm: "view_audit_log", testid: "nav-audit" },
    { to: "/health", label: "Health & Integrity", icon: Stethoscope, perm: "view_system_health", testid: "nav-health" },
    { to: "/protected-changes", label: "Protected Changes", icon: ShieldCheck, perm: "view_protected_changes", testid: "nav-protected-changes" },
    { to: "/putaway-log", label: "Parts Put Away", icon: MapPin, perm: "view_audit_log", testid: "nav-putaway-log" },
    { to: "/login-history", label: "Login History", icon: History, perm: "view_login_history", testid: "nav-login-history" },
  ] },
];

const linkClass = ({ isActive }) =>
  `flex items-center gap-2.5 px-3 py-2.5 text-sm whitespace-nowrap rounded-md transition-colors border-l-2 ${
    isActive
      ? "border-[#B01E28] bg-slate-800 text-white font-medium"
      : "border-transparent text-slate-300 hover:bg-slate-800/60 hover:text-white"
  }`;

function NavTree({ groups, has, onNavigate, badges = {} }) {
  const location = useLocation();
  const navigate = useNavigate();
  // HIDDEN, NOT DELETED (his instruction 2026-10-08): "Hide Work a Bin, Move Container and Move
  // Item now — nothing deleted, old links still work." The routes, the deep links from the scanned
  // location page and every bookmark keep working; they just stop cluttering the menu until the
  // four doors are retired into one. A hidden item is also skipped by the menu search.
  const visible = (items) => items.filter((n) => !n.hidden && (!n.perm || has(n.perm)));

  // Hybrid section link: arriving from elsewhere RESUMES your last view (filters + scroll,
  // via useScrollMemory); clicking the section you're already on CLEARS it to a fresh view.
  const goResumable = (base) => {
    const onList = location.pathname === base;
    try {
      if (onList) { sessionStorage.removeItem("scroll:" + base); sessionStorage.removeItem("lastview:" + base); }
    } catch { /* ignore */ }
    if (onList) { window.scrollTo(0, 0); navigate(base); }
    else {
      let last = null;
      try { last = sessionStorage.getItem("lastview:" + base); } catch { /* ignore */ }
      navigate(last || base);
    }
  };
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();

  // which titled group holds the current route
  const activeKey = useMemo(() => {
    for (const g of groups) {
      if (!g.title) continue;
      const hit = g.items.some((n) => (!n.perm || has(n.perm)) && (location.pathname === n.to || location.pathname.startsWith(n.to + "/")));
      if (hit) return g.title;
    }
    return null;
  }, [location.pathname, groups, has]);

  const [open, setOpen] = useState(() => {
    try { return JSON.parse(localStorage.getItem("navOpenGroups") || "{}"); } catch { return {}; }
  });
  // auto-expand the group containing the current route (default: only it is open)
  useEffect(() => {
    if (activeKey) setOpen((o) => (o[activeKey] ? o : { ...o, [activeKey]: true }));
  }, [activeKey]);
  // groups that start expanded even when they don't hold the active route
  const defaultOpenFor = (key) => key === activeKey || groups.some((g) => g.title === key && g.defaultOpen);
  const toggle = (key) => setOpen((o) => {
    const next = { ...o, [key]: !(key in o ? o[key] : defaultOpenFor(key)) };
    try { localStorage.setItem("navOpenGroups", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });
  const isOpen = (key) => (key in open ? open[key] : defaultOpenFor(key));

  const NavItem = (n) => {
    const exactlyOn = location.pathname === n.to;
    return (
      <NavLink key={n.to} to={n.to} data-testid={n.testid}
        onClick={(e) => { if (n.resumable) { e.preventDefault(); goResumable(n.to); } onNavigate(); }}
        className={linkClass}>
        <n.icon className="w-4 h-4 shrink-0" />
        <span className="flex-1 truncate">{n.label}</span>
        {n.resumable && exactlyOn && (
          <RotateCcw data-testid={`${n.testid}-reset-hint`} className="w-3.5 h-3.5 text-slate-400 hover:text-white shrink-0"
            title="You're here — click again to reset to a fresh view" />
        )}
        {n.badgeKey && badges[n.badgeKey] > 0 && (
          <span data-testid={`${n.testid}-badge`} className={`text-[10px] font-mono font-bold rounded-full px-1.5 min-w-[18px] text-center leading-[18px] ${n.badgeClass || "bg-[#B01E28] text-white"}`}>{badges[n.badgeKey]}</span>
        )}
      </NavLink>
    );
  };

  const groupBadgeTotal = (items) => items.reduce((s, n) => s + (n.badgeKey && badges[n.badgeKey] > 0 ? badges[n.badgeKey] : 0), 0);

  return (
    <>
      {/* filter box */}
      <div className="relative mb-1.5 px-1">
        <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input data-testid="nav-filter" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter menu…"
          className="w-full h-8 pl-8 pr-2 rounded-md bg-slate-800 text-sm text-slate-200 placeholder:text-slate-500 border border-slate-700 focus:border-[#B01E28] outline-none" />
      </div>
      {groups.map((g, gi) => {
        let items = visible(g.items);
        if (ql) items = items.filter((n) => n.label.toLowerCase().includes(ql));
        if (items.length === 0) return null;
        // untitled (Home) group — always shown, no header
        if (!g.title) return <div key={gi} className="flex flex-col gap-0.5">{items.map(NavItem)}</div>;
        const expanded = ql ? true : isOpen(g.title);
        const total = groupBadgeTotal(items);
        return (
          <div key={gi} className="mt-2 first:mt-0">
            <button data-testid={`nav-group-${g.title.replace(/[^a-z]+/gi, "-").toLowerCase()}`} onClick={() => !ql && toggle(g.title)}
              className="flex items-center gap-1 w-full px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-[#7D7D7D] hover:text-slate-200">
              {expanded ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
              <span className="flex-1 text-left truncate">{g.title}</span>
              {!expanded && total > 0 && <span className="text-[9px] font-bold rounded-full px-1.5 leading-[16px] bg-[#B01E28] text-white">{total}</span>}
            </button>
            {expanded && <div className="flex flex-col gap-0.5">{items.map(NavItem)}</div>}
          </div>
        );
      })}
    </>
  );
}

export function Layout({ children }) {
  const { user, has, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const floorReturn = useFloorReturn();
  const roundTrip = !!floorReturn?.active;
  // Remember your last view for the resumable sections, so a sidebar click can bring it back.
  useEffect(() => {
    const bases = ["/search", "/parts", "/donors", "/places"];
    if (bases.includes(location.pathname)) {
      try { sessionStorage.setItem("lastview:" + location.pathname, location.pathname + location.search); } catch { /* ignore */ }
    }
  }, [location.pathname, location.search]);
  const [adminOpen, setAdminOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openRequests, setOpenRequests] = useState(0);
  // THE FOOTER STAMP MUST NOT LAG THE DEPLOY. `BUILD_DATE` is a committed source file, so it read
  // the day the bundle was last stamped, not the day production went live (owner, 2026-09-30:
  // *"a date that lags the real deploy is a small lie"*). The backend's process start time cannot
  // lag — the code either restarted or it did not — so the footer reads that instead.
  const [liveSince, setLiveSince] = useState(null);
  useEffect(() => {
    api.get("/version").then(({ data }) => setLiveSince(data?.started_at || null)).catch(() => {});
  }, []);
  const [problemMoves, setProblemMoves] = useState(0);
  const [needsCompletion, setNeedsCompletion] = useState(0);

  useEffect(() => {
    if (!has("submit_request")) return;
    let alive = true;
    const fetchCount = () => { if (isPrintActive()) return; api.get("/requests/open-count").then(({ data }) => alive && setOpenRequests(data.open || 0)).catch(() => {}); };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [has]);

  useEffect(() => {
    if (!has("view_problem_moves")) return;
    let alive = true;
    const fetchCount = () => { if (isPrintActive()) return; api.get("/problem-moves/count").then(({ data }) => alive && setProblemMoves(data.unresolved_count || 0)).catch(() => {}); };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    window.addEventListener("problem-moves-changed", fetchCount);
    return () => { alive = false; clearInterval(t); window.removeEventListener("problem-moves-changed", fetchCount); };
  }, [has]);

  useEffect(() => {
    if (!has("build_cart") && !has("pick_remove")) return;
    let alive = true;
    const fetchCount = () => { if (isPrintActive()) return; api.get("/carts/completion-count").then(({ data }) => alive && setNeedsCompletion(data.count || 0)).catch(() => {}); };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [has]);

  const badges = { requests: openRequests, problems: problemMoves, needs_completion: needsCompletion };
  // STAGE 2: "sales" is the menu's word for "Stage 2 is switched on and you hold one of its six permissions"
  const sales = useSales();
  const navHas = useMemo(() => (cap) => (cap === "sales" ? sales.any : has(cap)), [has, sales.any]);

  // Live yard-time clock (updates each half-minute).
  const [clockTick, setClockTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setClockTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // "/" focuses the left-nav filter — but NEVER while typing in a field (guarded).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el?.isContentEditable) return;
      const input = document.querySelector('nav [data-testid="nav-filter"]');
      if (input) { e.preventDefault(); input.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const dir = getIdentity();
  const darkHeader = dir !== 1;
  const headerCls = DIRECTIONS[dir].headerClass;
  const logoSrc = LOGO_FULL;
  const nameCls = darkHeader ? "text-white" : "text-slate-900";
  const subCls = darkHeader ? "text-slate-400" : "text-slate-400";

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <ServerStatusBar />
      <header className={`${headerCls} border-t-2 border-[#A81018] sticky top-0 z-30 print:hidden shadow-sm`} data-identity={dir}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!roundTrip && (
              <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((o) => !o)} className={`md:hidden p-1 -ml-1 ${darkHeader ? "text-white" : "text-slate-700"}`} aria-label="Menu">
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            )}
            {/* In a Floor round-trip: an explicit, always-visible "back toward Floor" so a
                tech never reaches for the browser back button. Same guarded exit as the
                "Done" bar and the logo. */}
            {roundTrip && (
              <button data-testid="floor-return-top" onClick={() => floorReturn?.exitToFloor()}
                className={`flex items-center gap-1.5 rounded-lg h-9 px-3 font-bold text-sm active:scale-95 transition-transform duration-100 ${darkHeader ? "bg-white/10 text-white hover:bg-white/20" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                <ArrowLeft className="w-5 h-5" /> Floor
              </button>
            )}
            <div className="flex items-center gap-3 cursor-pointer" data-testid="header-logo" onClick={() => (roundTrip ? floorReturn?.exitToFloor() : navigate("/home"))}>
              <img src={logoSrc} alt="FAS — Used Truck Parts & Equipment" className="h-9 sm:h-10 w-auto" data-testid="brand-logo" />
              <span className={`hidden md:block h-7 w-px ${darkHeader ? "bg-white/15" : "bg-slate-200"}`} />
              <div className="leading-tight hidden md:block">
                <div className={`font-display font-black text-base tracking-tight ${nameCls}`}>{BRAND.portal}</div>
                <div className={`font-mono text-[9px] tracking-[0.2em] uppercase ${subCls}`}>{BRAND.heritageShort} · Friedrich's Auto &amp; Truck</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className={`hidden lg:flex flex-col items-end leading-none ${darkHeader ? "text-slate-300" : "text-slate-500"}`} data-testid="yard-clock" title={`Yard time — ${getYardTz()}`}>
              <span className="font-mono text-xs">{formatTime(new Date(clockTick).toISOString())}</span>
              <span className="font-mono text-[8px] uppercase tracking-[0.15em] opacity-70">Yard time</span>
            </div>
            <BackupHealthBadge dark={darkHeader} />
            <SoundToggle className="hidden sm:inline-flex bg-white/90" />
            {!roundTrip && <SurfaceToggle compact />}
            <NotificationBell />
            <Badge data-testid="user-role-badge" className={`rounded-full font-mono text-[10px] uppercase border-0 ${darkHeader ? "bg-white/10 text-white hover:bg-white/10" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}`}>{user?.role_name || user?.role}</Badge>
            <span className={`font-mono text-xs hidden md:inline ${darkHeader ? "text-slate-400" : "text-slate-500"}`} data-testid="user-email"><UserName email={user?.email} /></span>
            <Button data-testid="logout-button" variant="outline" size="sm" className={`rounded-lg h-8 px-2 sm:px-3 ${darkHeader ? "border-white/20 bg-transparent text-white hover:bg-white/10" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`} onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-20 print:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <nav data-testid="mobile-nav" className="absolute top-16 left-0 bottom-0 w-72 max-w-[85vw] bg-[#1F1F1F] p-3 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <NavTree groups={GROUPS} has={navHas} adminOpen={adminOpen} setAdminOpen={setAdminOpen} onNavigate={() => setMobileOpen(false)} badges={badges} />
          </nav>
        </div>
      )}

      <div className={`max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 gap-8 flex-1 ${roundTrip ? "" : "md:grid-cols-[224px_1fr]"} ${roundTrip ? "pb-28" : ""}`}>
        {!roundTrip && (
          <aside className="hidden md:block print:hidden">
            <nav className="bg-[#1F1F1F] rounded-xl p-2.5 flex flex-col gap-0.5 shadow-sm sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain nav-scroll">
              <NavTree groups={GROUPS} has={navHas} adminOpen={adminOpen} setAdminOpen={setAdminOpen} onNavigate={() => {}} badges={badges} />
            </nav>
          </aside>
        )}
        <main className="min-w-0">{children}</main>
      </div>

      {roundTrip && <FloorReturnBar />}

      {!roundTrip && <FloorNudge />}

      <footer data-testid="brand-footer" className="print:hidden border-t border-slate-200 bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.15em] uppercase text-slate-400 text-center">{BRAND.copyright}</span>
          <span className="text-slate-300" aria-hidden="true">·</span>
          <span data-testid="build-stamp" className="font-mono text-[10px] sm:text-[11px] tracking-[0.15em] uppercase text-slate-400" title="When the running code last started. Changes on every redeploy, so it cannot lag behind production.">
            {liveSince ? `Live since ${liveSince.slice(0, 16).replace("T", " ")} UTC` : "Live since —"}
          </span>
        </div>
      </footer>
    </div>
  );
}
